<template>
  <div class="WelcomeView container">
    <h1 class="WelcomeView__heading font-family-display font-weight-bold mb-48">
      {{ $t('welcome_back') }}
      <span class="text-brand-red">{{ user.nickName || user.firstName }}!</span>
    </h1>
    <template v-if="isLoading">
      <div class="flex justify-center my-24">
        <LoadingDots dots-bg-color-class="bg-brand-purple" />
      </div>
    </template>
    <template v-else>
      <div
        v-if="invitationsCount"
        class="WelcomeView__invitations flex space-between bg-success-bg text-success-text border-radius-8 pa-24 mb-24"
      >
        <div>
          <span class="icon-bell font-size-xl"></span>
          <h3 class="ml-16">
            You have {{ invitationsCount }} pending invitations
          </h3>
        </div>
        <router-link
          class="text-success-text text-underline"
          :to="{ name: 'InvitationsView' }"
        >
          <span class="ml-8"> Manage </span>
        </router-link>
      </div>
      <div v-if="stageConfigs" class="grid col-1 lg-col-2 mb-24 pb-80">
        <StageItemCard
          v-for="(stage, index) in stageConfigs"
          :key="`stage-item-card-${index}`"
          :fqdn="stage.stage.fqdn"
          :logo-url="stage.stage.logo"
          :artist-full-name="getOwnerArtist(stage)"
          :stage-members-list="getStageMembersList(stage)"
        />
        <StageItemCard :is-add-stage-btn="true" />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { LoadingDots } from '@snowshoe-frontend/shared-components'
import StageItemCard from '@/components/stage-item/StageItemCard.vue'

import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/userStore'
import { useStageStore } from '@/stores/stageStore'
import { useInvitationsStore } from '@/stores/invitationsStore'
import { useNotificationsStore } from '@/stores/notificationsStore'

const isLoading = ref(true)

const userStore = useUserStore()
const stageStore = useStageStore()
const invitationsStore = useInvitationsStore()
const { setNotification } = useNotificationsStore()

const { user } = storeToRefs(userStore)
const { stageConfigs } = storeToRefs(stageStore)
const { invitationsCount } = storeToRefs(invitationsStore)

function getOwnerArtist(stage) {
  const artistObj = stage.stage.members[0]?.employee

  if (!artistObj) return ''

  return `${artistObj.firstName} ${artistObj.lastName}`
}

function getStageMembersList(stage) {
  return stage.stage.members
}

onMounted(async () => {
  try {
    isLoading.value = true
    await userStore.getUserData()
    await stageStore.getStageConfigs()
    await invitationsStore.getInvitationsCount()
  } catch (e) {
    setNotification({
      header: e.message,
      body: 'Whoops! Please try again.',
      type: 'danger'
    })
  } finally {
    isLoading.value = false
  }
})
</script>

<style lang="scss">
.WelcomeView {
  &__heading {
    font-size: var(--font-size-xxl);

    @include desktop {
      font-size: var(--font-size-xxxxl);
    }
  }
  &__invitations {
    box-shadow: 0 var(--grid-4) 6px rgba(0, 0, 0, 0.04);
    align-items: center;

    > div {
      display: flex;
      align-items: center;
    }
  }
}
</style>
